import {
    getCopySinkModelFromDatasetType,
    getCopySourceModelFromDatasetAndLinkedServiceType
} from 'app/authoring/component/activities/copy-activity/util';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { FormatTypeCopySink } from 'app/model/pipeline/copysink/format-type.copysink';
import { FormatTypeCopySource } from 'app/model/pipeline/copysource/format-type.copysource';
import { GetMetadataActivity } from 'app/model/pipeline/get-metadata.activity';
import { LookupActivity } from 'app/model/pipeline/lookup.activity';
import {
    type Activity,
    CopySink,
    CopySource,
    type Dataset,
    FormatDatasetV2,
    IModel,
    LinkedService,
    Metamodel,
    Owned,
    Pipeline
} from 'shell-models-lib';
import { getModelByLinkedServiceType, getReferencedModelByDatasetType } from './model-converter.util';
import { StoreWriteSettings } from 'app/model/pipeline/copysink/store-write-settings/store-write-settings';
import { StoreReadSettings } from 'app/model/pipeline/copysource/store-read-settings/store-read-settings';
import { FormatReadSettings } from 'app/model/pipeline/copysource/format-read-settings/format-read-settings';
import { FormatWriteSettings } from 'app/model/pipeline/copysink/format-write-settings/format-write-settings';
import type { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { IConnectionContext, IUnknownConnectionContext } from 'app/interfaces/copilot.pipeline.action.types';
import { firstValueFrom, of, timeout } from 'rxjs';
import { type ModelService } from 'app/model/services/model.service';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { IFrameService } from 'app/services/iframe.service';

export async function findLinkedServiceFromContext<T extends LinkedService = LinkedService>(
    connectionContext: IConnectionContext,
    services: { modelService: ModelService; iframeService: IFrameService }
): Promise<{ linkedService: T; linkedServiceType: string }> {
    const linkedServices =
        ((await firstValueFrom(
            services.modelService.list(undefined, LinkedService.MODEL).pipe(timeout({ each: 5, with: () => of([]) }))
        )) as LinkedService[]) || [];
    const unifiedConnectionType =
        connectionContext.Type === 'LakehouseDb' || connectionContext.Type === 'LakehouseFiles'
            ? adfModelTypeMap.lakehouse.linkedServiceType
            : connectionContext.Type === 'Unknown'
              ? (connectionContext as IUnknownConnectionContext).OriginalConnectionType
              : connectionContext.Type;
    const lsModelTypeToSearch =
        services.iframeService.currentCustomerProfile.connectionTypeToLinkedServiceMapping[
            services.iframeService.currentCustomerProfile.linkedServiceToConnectionTypeMapping[unifiedConnectionType]
        ] ?? unifiedConnectionType;
    return {
        linkedService: linkedServices.find(
            (ls) => ls.name === connectionContext.Name && ls.getLinkedServiceType() === lsModelTypeToSearch
        ) as T,
        linkedServiceType: unifiedConnectionType
    };
}

export async function findPipelineFromContext<T extends Pipeline = Pipeline>(
    connectionContext: { PipelineName: string; PipelineId: string },
    services: { modelService: ModelService }
): Promise<T> {
    const pipelines =
        ((await firstValueFrom(
            services.modelService.list(undefined, Pipeline.MODEL).pipe(timeout({ each: 5, with: () => of([]) }))
        )) as Pipeline[]) || [];
    return pipelines.find(
        (pipeline) =>
            pipeline.name === connectionContext.PipelineName || pipeline.objectId === connectionContext.PipelineId
    ) as T;
}

export function setDbFromLs(entity: Dataset | Activity, linkedService: LinkedService) {
    if (entity && linkedService) {
        const lsDbValue = (
            Metamodel.modelOf(linkedService).hasAttribute('databaseName')
                ? Metamodel.modelOf(linkedService).attribute('databaseName')
                : undefined
        )?.value(linkedService);
        const entityDbAttribute = Metamodel.modelOf(entity).hasAttribute('database')
            ? Metamodel.modelOf(entity).attribute('database')
            : undefined;
        const dsDbValueOrTemplateValue = entityDbAttribute?.value(entity) ?? entityDbAttribute?.templateValue(entity);
        if (lsDbValue && entityDbAttribute && !dsDbValueOrTemplateValue) {
            ValueAdapter.toModel(entity, entityDbAttribute, lsDbValue);
        }
    }
}

export function setSourceDataset(
    ownerActivity: CopyActivity | LookupActivity,
    dataset: Dataset,
    services: { linkedServiceRegistry: LinkedServiceRegistryService }
) {
    ownerActivity.sourceDataset = dataset;
    const lsConfig = dataset.linkedService
        ? services.linkedServiceRegistry.getLinkedServiceConfiguration(Metamodel.modelOf(dataset.linkedService).name)
        : undefined;
    const newCopySourceModel: IModel = getCopySourceModelFromDatasetAndLinkedServiceType(
        Metamodel.modelOf(dataset).name,
        lsConfig
    );
    ownerActivity.source =
        newCopySourceModel != null ? Owned.create<CopySource>(newCopySourceModel, ownerActivity) : undefined;
    setStoreAndFormatSettings(ownerActivity);
}

export function setStoreAndFormatSettings(
    ownerActivity: CopyActivity | LookupActivity | GetMetadataActivity,
    isCopySink: boolean = false
) {
    const dataset =
        ownerActivity instanceof GetMetadataActivity
            ? ownerActivity.dataset
            : ownerActivity instanceof CopyActivity && isCopySink
              ? ownerActivity.sinkDataset
              : ownerActivity.sourceDataset;
    const settingsOwner =
        ownerActivity instanceof GetMetadataActivity
            ? ownerActivity
            : ownerActivity instanceof CopyActivity && isCopySink
              ? (ownerActivity.copySink as FormatTypeCopySink)
              : (ownerActivity.source as FormatTypeCopySource);
    if (dataset instanceof FormatDatasetV2) {
        const storeSettingsModel = getModelByLinkedServiceType(
            dataset.linkedService.getLinkedServiceType(),
            isCopySink ? StoreWriteSettings.MODEL : StoreReadSettings.MODEL
        );
        settingsOwner.storeSettings = storeSettingsModel ? Owned.create(storeSettingsModel, settingsOwner) : undefined;

        const formatSettingsModel = getReferencedModelByDatasetType(
            Metamodel.modelOf(dataset).name,
            isCopySink ? FormatWriteSettings.MODEL : FormatReadSettings.MODEL
        );
        settingsOwner.formatSettings = formatSettingsModel
            ? Owned.create(formatSettingsModel, settingsOwner)
            : undefined;
    }
}

export function setDestinationDataset(ownerActivity: CopyActivity, dataset: Dataset) {
    ownerActivity.sinkDataset = dataset;
    const sinkModel = getCopySinkModelFromDatasetType(Metamodel.modelOf(dataset).name);
    ownerActivity.copySink = sinkModel ? Owned.create<CopySink>(sinkModel, ownerActivity) : undefined;
    setStoreAndFormatSettings(ownerActivity, true);
}

export function setGetMetadataDataset(activity: GetMetadataActivity, dataset: Dataset) {
    activity.dataset = dataset;
    setStoreAndFormatSettings(activity);
}
